import React, { useState, useEffect,useRef } from 'react';
import { 
  Grid, Box, Typography, Paper, Select, MenuItem, FormControl, 
  InputLabel, Button, CircularProgress, Divider, useTheme,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete
} from '@mui/material';
import { loamsApi } from '@/services/api';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import './ReportDashboard.css';
import html2pdf from 'html2pdf.js';

const ReportDashboard = () => {
  const theme = useTheme();
  
  // State management
  const [viewType, setViewType] = useState('monthly'); // 'monthly', 'yearly', or 'project'
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectInput, setProjectInput] = useState('');
  const reportContentRef = useRef(null);
  const [exportFormat, setExportFormat] = useState('excel'); // 'excel' or 'pdf'
  // Filter states for monthly view
  const [filters, setFilters] = useState({
    cluster: '',
    poNumber: '',
    invoiceNumber: '',
    ccNumber: '',
    status: ''
  });

  // Years for dropdown (2023 to current year + 1)
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: (currentYear + 1) - 2023 + 1 },
    (_, i) => 2023 + i
  );
  
  // Months for dropdown
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Fetch projects for dropdown
  const fetchProjects = async (searchTerm = '') => {
    try {
      const projects = await loamsApi.getProjectsList(searchTerm);
      setProjects(projects);
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  };
  
  // Fetch projects on initial load
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch monthly report data
  const fetchMonthlyReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loamsApi.getMonthlyReportData(year, month);
      setMonthlyData(data);
    } catch (err) {
      setError(err.message || 'Failed to load monthly report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch yearly report data
  const fetchYearlyReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await loamsApi.getYearlyReportData(year);
      setYearlyData(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load yearly report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch project report data
  const fetchProjectReport = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await loamsApi.getProjectReportData(selectedProject.po_number);
      setProjectData(data);
    } catch (err) {
      setError(err.message || 'Failed to load project report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleMonthlyExport = async () => {
    try {
      setLoading(true);
      await loamsApi.exportMonthlyReport(year, month);
      setLoading(false);
    } catch (err) {
      setError('Failed to export report. Please try again.');
      setLoading(false);
    }
  };

  // Export monthly report to Excel
  const handleMonthlyPdfExport = async () => {
    setLoading(true);
    try {
      // If no report data, fetch it first
      if (!monthlyData) {
        await fetchMonthlyReport();
      }
  
      // Import necessary libraries
      const jspdfModule = await import('jspdf');
      const jsPDF = jspdfModule.default;
      const autotableModule = await import('jspdf-autotable');
      const autoTable = autotableModule.default;
      
      // Create new PDF document
      const doc = new jsPDF();
      
      // Add company header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PHOENICIAN TECHNICAL SERVICES LLC', 105, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Central Plaza, Office 399, Dubai Investment Park 1', 105, 28, { align: 'center' });
      doc.text('+971 4 8882 9464 B4', 105, 34, { align: 'center' });
      
      // Add report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Report per Project', 105, 45, { align: 'center' });
      doc.setFontSize(12);
      const monthName = getMonthName(month);
      doc.text(`${monthName} ${year}`, 105, 52, { align: 'center' });
      
      // Create summary table
      const summaryData = [
        ['', 'Count', 'Amount (AED)'],
        ['Active Projects', monthlyData.summary.activeProjects || 0, formatCurrency(monthlyData.summary.activeAmount || 0)],
        ['Completion Certificates', monthlyData.summary.completionCertificates || 0, formatCurrency(monthlyData.summary.certificateAmount || 0)],
        ['Tax Invoice Issued', monthlyData.summary.invoicesIssued || 0, formatCurrency(monthlyData.summary.invoiceAmount || 0)],
        ['Payment Received', monthlyData.summary.paymentsReceived || 0, formatCurrency(monthlyData.summary.paymentAmount || 0)]
      ];
      
      // Add summary table
      autoTable(doc, {
        startY: 65,
        head: [summaryData[0]],
        body: summaryData.slice(1),
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [162, 115, 46], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 50, halign: 'right' }
        }
      });
      
      // Get the Y position after the summary table
      const finalY = doc.previousAutoTable.finalY || 120;
      
      // Add details section title
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Details', 14, finalY + 15);
      
      // Prepare details data
      const detailsRows = [];
      
      if (monthlyData.details && monthlyData.details.length > 0) {
        monthlyData.details.forEach(item => {
          detailsRows.push([
            item.cluster || '-',
            item.poNumber || '-',
            item.invoiceNumber || '-',
            item.ccNumber || '-',
            item.status || '-',
            formatCurrency(item.amount || 0)
          ]);
        });
      }
      
      // Add details table
      autoTable(doc, {
        startY: finalY + 20,
        head: [['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount']],
        body: detailsRows,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [162, 115, 46], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 'auto', halign: 'right' }
        },
        didDrawPage: (data) => {
          // Add header to each new page
          if (data.pageNumber > 1) {
            doc.setFontSize(8);
            doc.text(`Monthly Report - ${monthName} ${year}`, 105, 10, { align: 'center' });
          }
        }
      });
      
      // Save the PDF
      doc.save(`Monthly_Report_${year}_${monthName}.pdf`);
      
      setLoading(false);
      console.log('PDF export completed successfully');
    } catch (err) {
      console.error('PDF export preparation error:', err);
      setLoading(false);
      setError('Failed to prepare PDF export. Falling back to Excel export.');
      // Fallback to Excel export
      handleMonthlyExport();
    }
  };

  // Export yearly report to Excel
  const handleYearlyExport = async () => {
    try {
      setLoading(true);
      await loamsApi.exportYearlyReport(year);
      setLoading(false);
    } catch (err) {
      setError('Failed to export report. Please try again.');
      setLoading(false);
    }
  };

  // Add this function to your component to handle yearly report PDF export
  // Complete updated yearly PDF export function
// Complete fixed yearly PDF export function
// Complete fixed yearly PDF export function with all requirements
const handleYearlyPdfExport = async () => {
  setLoading(true);
  try {
    // If no yearly data, fetch it first
    if (!yearlyData) {
      await fetchYearlyReport();
    }
    
    // Import necessary libraries
    const jspdfModule = await import('jspdf');
    const jsPDF = jspdfModule.default;
    const autotableModule = await import('jspdf-autotable');
    const autoTable = autotableModule.default;
    
    // Create new PDF document in landscape orientation with A3 format
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3' // Use A3 for more space to fit all columns properly
    });
    
    // Function to get logo as data URL
    const getLogoDataUrl = async () => {
      try {
        // Create a Canvas element
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Create an Image element
        const img = new Image();
        img.src = phoenicianLogo;
        
        // Wait for the image to load
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        // Set canvas dimensions to match the image
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);
        
        // Get data URL from canvas
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.error('Error preparing logo:', error);
        return null;
      }
    };
    
    // Get logo data URL once
    const logoDataUrl = await getLogoDataUrl();
    
    // Utility function to add page header with logo and download info
    const addFirstPageHeader = () => {
      // Add company logo to top right (only on first page)
      try {
        // Position and dimensions
        const logoX = doc.internal.pageSize.width - 40; // 40mm from right edge
        const logoY = 15; // 15mm from top
        const logoWidth = 30;
        const logoHeight = 20;
        
        if (logoDataUrl) {
          // Add the actual logo image
          doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
        } else {
          // Fallback if logo can't be loaded
          doc.setFillColor(200, 200, 200);
          doc.roundedRect(logoX, logoY, logoWidth, logoHeight, 1, 1, 'F');
          doc.setTextColor(100, 100, 100);
          doc.setFontSize(8);
          doc.text('LOGO', logoX + logoWidth/2, logoY + logoHeight/2, { align: 'center' });
        }
      } catch (error) {
        console.warn('Could not add logo to PDF', error);
      }
      
      // Add company header text
      doc.setFontSize(18);
      doc.setTextColor(162, 115, 46); // Gold brand color (#A2732E)
      doc.setFont('helvetica', 'bold');
      doc.text('PHOENICIAN TECHNICAL SERVICES LLC', doc.internal.pageSize.width / 2, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0); // Back to black
      doc.text('Central Plaza, Office 399, Dubai Investment Park 1', doc.internal.pageSize.width / 2, 28, { align: 'center' });
      doc.text('+971 4 8882 9464 B4', doc.internal.pageSize.width / 2, 34, { align: 'center' });
      
      // Add report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(162, 115, 46); // Gold brand color
      doc.text('Yearly Summary Report ' + year, doc.internal.pageSize.width / 2, 45, { align: 'center' });
    };
    
    // Function to add download info at bottom of page
    const addDownloadInfo = () => {
      const now = new Date();
      // Format date according to Dubai time (UTC+4)
      const dubaiTime = new Date(now.getTime() + (4 * 60 * 60 * 1000)); // UTC+4 for Dubai
      const dateStr = dubaiTime.toLocaleString('en-AE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Dubai'
      });
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text(`Downloaded by - Antony (ACCOUNTS) | ${dateStr}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
    };
    
    // Add first page header
    addFirstPageHeader();
    
    // Summary table data
    const summaryData = [
      {
        title: 'Active Projects',
        description: 'Valid Service Agreements',
        count: yearlyData.summary.activeProjects.count,
        amount: formatCurrency(yearlyData.summary.activeProjects.amount)
      },
      {
        title: 'Waived by Management',
        description: 'Projects without Operation for the month',
        count: yearlyData.summary.waivedByManagement?.count || 0,
        amount: formatCurrency(yearlyData.summary.waivedByManagement?.amount || 0)
      },
      {
        title: 'To be Invoiced',
        description: 'Operation for the Month',
        count: Math.max(0, yearlyData.summary.activeProjects.count - (yearlyData.summary.waivedByManagement?.count || 0)),
        amount: formatCurrency(Math.max(0, yearlyData.summary.activeProjects.amount - (yearlyData.summary.waivedByManagement?.amount || 0)))
      },
      {
        title: 'No Completion Certificate',
        description: 'No Completion Certificate Received from site',
        count: yearlyData.summary.noCompletionCertificate?.count || 0,
        amount: formatCurrency(yearlyData.summary.noCompletionCertificate?.amount || 0)
      },
      {
        title: 'Completion Certificates',
        description: 'Completion Certificate is received from site',
        count: yearlyData.summary.completionCertificates.count,
        amount: formatCurrency(yearlyData.summary.completionCertificates.amount)
      },
      {
        title: 'Invoice Not Processed',
        description: 'Invoice already issued but not yet processed for approval/payment',
        count: yearlyData.summary.invoiceNotProcessed?.count || 0,
        amount: formatCurrency(yearlyData.summary.invoiceNotProcessed?.amount || 0)
      },
      {
        title: 'Invoice Processed',
        description: 'Invoice is already uploaded to Portal or submitted to FM for approval',
        count: yearlyData.summary.invoiceProcessed?.count || 0,
        amount: formatCurrency(yearlyData.summary.invoiceProcessed?.amount || 0)
      },
      {
        title: 'Payment Received',
        description: 'Payment is already reflected to PTS\' Bank Statement',
        count: yearlyData.summary.paymentsReceived.count,
        amount: formatCurrency(yearlyData.summary.paymentsReceived.amount)
      },
      {
        title: 'Balance Receivable',
        description: 'Balance from Invoice-Processed',
        count: (yearlyData.summary.invoiceProcessed?.count || 0) - yearlyData.summary.paymentsReceived.count,
        amount: formatCurrency((yearlyData.summary.invoiceProcessed?.amount || 0) - yearlyData.summary.paymentsReceived.amount)
      }
    ];
    
    // Manual rendering of the summary table to avoid duplication issues
    const pageWidth = doc.internal.pageSize.width;
    const pageMargin = 20;
    const tableWidth = pageWidth - (pageMargin * 2);
    const tableStartY = 60;
    const colWidths = [tableWidth * 0.6, tableWidth * 0.15, tableWidth * 0.25];
    const rowHeight = 15;
    
    // Draw table header
    doc.setFillColor(162, 115, 46); // Gold brand color for header
    doc.rect(pageMargin, tableStartY, tableWidth, rowHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255); // White text for header
    doc.setFontSize(11);
    doc.text('', pageMargin + 5, tableStartY + (rowHeight / 2) + 3);
    doc.text('Count', pageMargin + colWidths[0] + (colWidths[1] / 2), tableStartY + (rowHeight / 2) + 3, { align: 'center' });
    doc.text('Amount', pageMargin + colWidths[0] + colWidths[1] + (colWidths[2] / 2), tableStartY + (rowHeight / 2) + 3, { align: 'center' });
    
    // Draw table borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Horizontal lines
    doc.line(pageMargin, tableStartY, pageMargin + tableWidth, tableStartY); // Top line
    doc.line(pageMargin, tableStartY + rowHeight, pageMargin + tableWidth, tableStartY + rowHeight); // After header
    
    // Vertical lines
    doc.line(pageMargin, tableStartY, pageMargin, tableStartY + rowHeight); // Left edge
    doc.line(pageMargin + colWidths[0], tableStartY, pageMargin + colWidths[0], tableStartY + rowHeight); // After title column
    doc.line(pageMargin + colWidths[0] + colWidths[1], tableStartY, pageMargin + colWidths[0] + colWidths[1], tableStartY + rowHeight); // After count column
    doc.line(pageMargin + tableWidth, tableStartY, pageMargin + tableWidth, tableStartY + rowHeight); // Right edge
    
    // Draw data rows
    let currentY = tableStartY + rowHeight;
    
    summaryData.forEach((row, index) => {
      const isEvenRow = index % 2 === 0;
      
      // Set background color for even rows
      if (isEvenRow) {
        doc.setFillColor(245, 245, 245);
        doc.rect(pageMargin, currentY, tableWidth, rowHeight, 'F');
      }
      
      // Draw main title
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(row.title, pageMargin + 5, currentY + 5);
      
      // Draw description below in gold and italic
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(162, 115, 46); // Gold brand color
      doc.setFontSize(8);
      doc.text(row.description, pageMargin + 5, currentY + 10);
      
      // Draw count
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(
        String(row.count), 
        pageMargin + colWidths[0] + (colWidths[1] / 2), 
        currentY + (rowHeight / 2) + 2, 
        { align: 'center' }
      );
      
      // Draw amount
      doc.text(
        `AED ${row.amount}`, 
        pageMargin + colWidths[0] + colWidths[1] + colWidths[2] - 5, 
        currentY + (rowHeight / 2) + 2, 
        { align: 'right' }
      );
      
      // Draw horizontal line after row
      doc.line(pageMargin, currentY + rowHeight, pageMargin + tableWidth, currentY + rowHeight);
      
      // Draw vertical lines for this row
      doc.line(pageMargin, currentY, pageMargin, currentY + rowHeight); // Left edge
      doc.line(pageMargin + colWidths[0], currentY, pageMargin + colWidths[0], currentY + rowHeight); // After title column
      doc.line(pageMargin + colWidths[0] + colWidths[1], currentY, pageMargin + colWidths[0] + colWidths[1], currentY + rowHeight); // After count column
      doc.line(pageMargin + tableWidth, currentY, pageMargin + tableWidth, currentY + rowHeight); // Right edge
      
      // Move to next row
      currentY += rowHeight;
    });
    
    // Add download info to first page
    addDownloadInfo();
    
    // Add second page for monthly data
    doc.addPage();
    
    // Add section title for Monthly Breakdown - Amount
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Summary (Amount)', 20, 20);
    
    // Create monthly amount table with shorter descriptions to fit in the PDF
    const monthlyAmountHeaders = [
      [
        { content: 'Month', styles: { halign: 'left', fontStyle: 'bold' } },
        { content: 'Active Projects', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Waived by Management', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'To be Invoiced', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'No Completion Certificate', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Received Completion Certificate', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Invoice-Not Processed', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Invoice-Processed', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Payment Received', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: 'Balance Receivable', styles: { halign: 'right', fontStyle: 'bold' } }
      ],
      [
        { content: '', styles: { fontStyle: 'italic' } },
        { content: 'Valid Service Agreements', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Projects without Operation', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Operation for Month', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'No Certificate from Site', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Certificate received', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Invoice issued not processed', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Invoice uploaded or submitted', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Payment reflected in Bank', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } },
        { content: 'Balance from Invoice', styles: { fontStyle: 'italic', halign: 'right', textColor: [162, 115, 46] } }
      ]
    ];
    
    // Prepare the monthly amount data
    const monthlyAmountData = yearlyData.monthly_details.map(month => {
      // Calculate Balance Receivable as Invoice Processed - Payment Received
      const balanceReceivable = (month.processed_amount || 0) - (month.paid_amount || 0);
      
      return [
        { content: month.month.trim(), styles: { halign: 'left' } },
        { content: formatCurrency(month.active_projects_amount), styles: { halign: 'right' } },
        { content: formatCurrency(month.waived_amount), styles: { halign: 'right', textColor: [174, 39, 50] } }, // Red for waived
        { 
          content: formatCurrency(Math.max(0, (month.active_projects_amount || 0) - (month.waived_amount || 0))), 
          styles: { halign: 'right' } 
        },
        { content: formatCurrency(month.no_cc_amount), styles: { halign: 'right', textColor: [174, 39, 50] } }, // Red for no CC
        { content: formatCurrency(month.cc_amount), styles: { halign: 'right' } },
        { content: formatCurrency(month.not_processed_amount), styles: { halign: 'right', textColor: [174, 39, 50] } }, // Red for not processed
        { content: formatCurrency(month.processed_amount), styles: { halign: 'right' } },
        { content: formatCurrency(month.paid_amount), styles: { halign: 'right' } },
        { content: formatCurrency(balanceReceivable), styles: { halign: 'right', textColor: [174, 39, 50] } } // Red for receivable
      ];
    });
    
    // Add total row
    const totalRow = [
      { content: 'Total', styles: { halign: 'left', fontStyle: 'bold' } },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.active_projects_amount > 0)
            .reduce((sum, month) => sum + month.active_projects_amount, 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.waived_amount > 0)
            .reduce((sum, month) => sum + (month.waived_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => (month.active_projects_amount - (month.waived_amount || 0)) > 0)
            .reduce((sum, month) => sum + Math.max(0, month.active_projects_amount - (month.waived_amount || 0)), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.no_cc_amount > 0)
            .reduce((sum, month) => sum + (month.no_cc_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.cc_amount > 0)
            .reduce((sum, month) => sum + (month.cc_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.not_processed_amount > 0)
            .reduce((sum, month) => sum + (month.not_processed_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.processed_amount > 0)
            .reduce((sum, month) => sum + (month.processed_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => month.paid_amount > 0)
            .reduce((sum, month) => sum + (month.paid_amount || 0), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      },
      { 
        content: formatCurrency(
          yearlyData.monthly_details
            .filter(month => (month.processed_amount - month.paid_amount) > 0)
            .reduce((sum, month) => sum + Math.max(0, (month.processed_amount || 0) - (month.paid_amount || 0)), 0)
        ), 
        styles: { halign: 'right', fontStyle: 'bold' } 
      }
    ];
    
    // Add monthly amount table with descriptions
    autoTable(doc, {
      startY: 30, // Start lower to give space for the title
      head: monthlyAmountHeaders,
      body: [...monthlyAmountData, totalRow],
      theme: 'grid',
      styles: { 
        fontSize: 8,
        cellPadding: 2,
        overflow: 'ellipsize',
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: { 
        fillColor: [162, 115, 46], 
        textColor: [255, 255, 255],
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      // Make header rows in table have background color
      willDrawCell: function(data) {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = [162, 115, 46];
          data.cell.styles.textColor = [255, 255, 255];
        }
      }
    });
    
    // Ensure the downloaded by info appears on the second page as well
    addDownloadInfo();
    
    // Add a third page for the count summary
    doc.addPage();
    
    // Add section title for Monthly Breakdown - Count
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Summary (Count)', 20, 20);
    
    // Define column headers for the count breakdown with shorter descriptions
    const countHeaders = [
      [
        { content: 'Month', styles: { halign: 'left', fontStyle: 'bold' } },
        { content: 'Active Projects', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Waived by Management', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'To be Invoiced', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'No Completion Certificate', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Received Completion Certificate', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Invoice-Not Processed', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Invoice-Processed', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Payment Received', styles: { halign: 'center', fontStyle: 'bold' } },
        { content: 'Balance Receivable', styles: { halign: 'center', fontStyle: 'bold' } }
      ],
      [
        { content: '', styles: { fontStyle: 'italic' } },
        { content: 'Valid Service Agreements', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Projects without Operation', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Operation for Month', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'No Certificate from Site', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Certificate received', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Invoice issued not processed', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Invoice uploaded or submitted', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Payment reflected in Bank', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } },
        { content: 'Balance from Invoice', styles: { fontStyle: 'italic', halign: 'center', textColor: [162, 115, 46] } }
      ]
    ];
    
    // Prepare the monthly count data
    const monthlyCountData = yearlyData.monthly_details.map(month => {
      // Calculate "To be Invoiced" count for each month
      const toBeInvoicedCount = Math.max(0, (month.active_projects_count || 0) - (month.waived_count || 0));
      
      // Calculate Balance Receivable count as Processed - Paid
      const receivableCount = (month.processed_count || 0) - (month.paid_count || 0);
      
      return [
        { content: month.month.trim(), styles: { halign: 'left' } },
        { content: month.active_projects_count, styles: { halign: 'center' } },
        { content: month.waived_count, styles: { halign: 'center', textColor: [174, 39, 50] } }, // Red for waived
        { content: toBeInvoicedCount, styles: { halign: 'center' } },
        { content: month.no_cc_count, styles: { halign: 'center', textColor: [174, 39, 50] } }, // Red for no CC
        { content: month.cc_count, styles: { halign: 'center' } },
        { content: month.not_processed_count, styles: { halign: 'center', textColor: [174, 39, 50] } }, // Red for not processed
        { content: month.processed_count, styles: { halign: 'center' } },
        { content: month.paid_count, styles: { halign: 'center' } },
        { content: receivableCount, styles: { halign: 'center', textColor: [174, 39, 50] } } // Red for receivable
      ];
    });
    
    // Add total count row
    const totalCountRow = [
      { content: 'Total', styles: { halign: 'left', fontStyle: 'bold' } },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + month.active_projects_count, 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + (month.waived_count || 0), 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: Math.max(
          0, 
          Math.max(...yearlyData.monthly_details.map(m => m.active_projects_count)) - 
          yearlyData.monthly_details.reduce((sum, month) => sum + (month.waived_count || 0), 0)
        ),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + (month.no_cc_count || 0), 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + month.cc_count, 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + (month.not_processed_count || 0), 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + (month.processed_count || 0), 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: yearlyData.monthly_details.reduce((sum, month) => sum + month.paid_count, 0),
        styles: { halign: 'center', fontStyle: 'bold' } 
      },
      { 
        content: Math.max(0, 
          yearlyData.monthly_details.reduce((sum, month) => sum + (month.processed_count || 0), 0) - 
          yearlyData.monthly_details.reduce((sum, month) => sum + month.paid_count, 0)
        ),
        styles: { halign: 'center', fontStyle: 'bold' } 
      }
    ];
    
    // Add monthly count table
    autoTable(doc, {
      startY: 30, // Start lower to give space for the title
      head: countHeaders,
      body: [...monthlyCountData, totalCountRow],
      theme: 'grid',
      styles: { 
        fontSize: 8,
        cellPadding: 2,
        overflow: 'ellipsize',
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      headStyles: { 
        fillColor: [162, 115, 46], 
        textColor: [255, 255, 255],
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      // Make header rows in table have background color
      willDrawCell: function(data) {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = [162, 115, 46];
          data.cell.styles.textColor = [255, 255, 255];
        }
      }
    });
    
    // Add download info to final page too
    addDownloadInfo();
    
    // Save the PDF
    doc.save(`Yearly_Report_${year}.pdf`);
    
    setLoading(false);
    console.log('Yearly PDF export completed successfully');
  } catch (err) {
    console.error('Yearly PDF export error:', err);
    setLoading(false);
    setError('Failed to generate PDF. Falling back to Excel export.');
    // Fallback to Excel export
    handleYearlyExport();
  }
};
// Alternative implementation using jsPDF directly for better control








  
  // Export project report to Excel
  const handleProjectExport = async () => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      await loamsApi.exportProjectReport(selectedProject.po_number);
      setLoading(false);
    } catch (err) {
      setError('Failed to export report. Please try again.');
      setLoading(false);
    }
  };

  // Fetch data based on selected view type
  const fetchReport = () => {
    if (viewType === 'yearly') {
      fetchYearlyReport();
    } else if (viewType === 'project') {
      if (selectedProject) {
        fetchProjectReport();
      }
    } else {
      fetchMonthlyReport();
    }
  };

  // Initial data load
  useEffect(() => {
    if (viewType !== 'project' || selectedProject) {
      fetchReport();
    }
  }, [viewType, year, selectedProject]);

  // Paper style for A4-like section
  const a4SectionStyle = {
    width: '100%',
    maxWidth: viewType === 'yearly' ? '1500px' : '794px', // Wider for yearly report (landscape)
    minHeight: '600px',
    margin: '0 auto',
    padding: '40px',
    overflowX: 'auto',
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    position: 'relative',
    backgroundColor: '#fff',
    pageBreakAfter: 'always',
    // Print settings for landscape orientation
    '@media print': {
      size: viewType === 'yearly' ? 'landscape' : 'portrait',
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Get month name
  const getMonthName = (monthNum) => {
    return new Date(2000, monthNum - 1, 1).toLocaleString('default', { month: 'long' });
  };

  // Filter handling for monthly view
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter data for monthly view
  const filterData = (data) => {
    if (!data) return [];
    return data.filter(item => {
      return (
        item.cluster.toLowerCase().includes(filters.cluster.toLowerCase()) &&
        item.poNumber.toLowerCase().includes(filters.poNumber.toLowerCase()) &&
        (item.invoiceNumber || '').toLowerCase().includes(filters.invoiceNumber.toLowerCase()) &&
        (item.ccNumber || '').toLowerCase().includes(filters.ccNumber.toLowerCase()) &&
        (item.status || '').toLowerCase().includes(filters.status.toLowerCase())
      );
    });
  };

  // Handle project selection change
  const handleProjectChange = (event, newValue) => {
    setSelectedProject(newValue);
    if (newValue) {
      fetchProjectReport();
    } else {
      setProjectData(null);
    }
  };

  // Handle tab change
  const handleViewTypeChange = (newViewType) => {
    setViewType(newViewType);
    setError(null);
  };

  // Render the monthly report content
  const renderMonthlyReport = () => (
    <div className="print-content">
      {/* Header with Logo */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#A2732E' }} className="brand-color">
            PHOENICIAN TECHNICAL SERVICES LLC
          </Typography>
          <Typography variant="body2">Central Plaza, Office 399, Dubai Investment Park 1</Typography>
          <Typography variant="body2">+971 4 8882 9464 B4</Typography>
        </Box>
        <Box>
          <img src={phoenicianLogo} alt="Phoenician Logo" style={{ height: '60px' }} />
        </Box>
      </Box>
  
      <Typography variant="h5" fontWeight="bold" sx={{ color: '#A2732E' }} align="center" mb={1} className="brand-color">
        Monthly Report per Project
      </Typography>
      
      <Typography variant="h6" align="center" mb={3}>
        {getMonthName(month)} {year}
      </Typography>
  
      {monthlyData ? (
        <>
          {/* Summary Section */}
          <TableContainer component={Paper} elevation={0} sx={{ mb: 4 }} className="summary-table">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Count</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Amount</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight="medium">Active Projects</Typography>
                  </TableCell>
                  <TableCell align="center">{monthlyData.summary?.activeProjects || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(monthlyData.summary?.activeAmount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight="medium">Completion Certificates</Typography>
                  </TableCell>
                  <TableCell align="center">{monthlyData.summary?.completionCertificates || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(monthlyData.summary?.certificateAmount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight="medium">Tax Invoice Issued</Typography>
                  </TableCell>
                  <TableCell align="center">{monthlyData.summary?.invoicesIssued || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(monthlyData.summary?.invoiceAmount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight="medium">Payment Received</Typography>
                  </TableCell>
                  <TableCell align="center">{monthlyData.summary?.paymentsReceived || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(monthlyData.summary?.paymentAmount || 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
  
          <Divider sx={{ mb: 3 }} />
  
          {/* Filters Row - This will be hidden in print/export with hide-in-print class */}
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            gap: 2, 
            position: 'sticky', 
            top: 0, 
            backgroundColor: '#fff', 
            zIndex: 1, 
            py: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }} className="hide-in-print">
            <TextField
              size="small"
              label="Cluster"
              value={filters.cluster}
              onChange={(e) => handleFilterChange('cluster', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="PO No."
              value={filters.poNumber}
              onChange={(e) => handleFilterChange('poNumber', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Invoice No"
              value={filters.invoiceNumber}
              onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="CC No"
              value={filters.ccNumber}
              onChange={(e) => handleFilterChange('ccNumber', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>
  
          {/* Table Headers */}
          <Grid container sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 1,
            backgroundColor: '#f9f9f9',
            position: 'sticky',
            top: '64px',
            zIndex: 1
          }} className="table-header">
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">Cluster</Typography></Grid>
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">PO No.</Typography></Grid>
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">Invoice No</Typography></Grid>
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">CC No</Typography></Grid>
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">Status</Typography></Grid>
            <Grid item xs={2}><Typography variant="body2" fontWeight="bold">Amount</Typography></Grid>
          </Grid>
  
          {/* Screen View (filtered data) - Will be hidden in print */}
          <div className="hide-in-print">
            {monthlyData && filterData(monthlyData.details).map((item, index) => (
              <Grid container key={index} sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 0.75,
                '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }
              }}>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap title={item.cluster}>
                    {item.cluster}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap title={item.poNumber}>
                    {item.poNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap>
                    {item.invoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{
                    color: item.ccNumber === 'Yes' ? '#A2732E' : 'text.secondary'
                  }}>
                    {item.ccNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{
                    color: item.status === 'Paid' ? '#A2732E' : 'text.secondary'
                  }}>
                    {item.status}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" align="right">
                    AED {formatCurrency(item.amount)}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </div>
  
          {/* Print View (all data, unfiltered) - Only visible in print/export */}
          <div className="print-only">
            {monthlyData && monthlyData.details.map((item, index) => (
              <Grid container key={index} sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 0.75,
                '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }
              }}>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap title={item.cluster}>
                    {item.cluster}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap title={item.poNumber}>
                    {item.poNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" noWrap>
                    {item.invoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{
                    color: item.ccNumber === 'Yes' ? '#A2732E' : 'text.secondary'
                  }} className={item.ccNumber === 'Yes' ? 'cc-yes' : 'cc-no'}>
                    {item.ccNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{
                    color: item.status === 'Paid' ? '#A2732E' : 'text.secondary'
                  }} className={item.status === 'Paid' ? 'status-paid' : 'status-other'}>
                    {item.status}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" align="right">
                    AED {formatCurrency(item.amount)}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </div>
        </>
      ) : (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            Select year and month and click Apply to view report
          </Typography>
        </Box>
      )}
    </div>
  );

  // Render the yearly report content
  // Render the yearly report content with added status columns
  const renderYearlyReport = () => (
    <>
      {/* Header with Logo */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#A2732E' }}>
            PHOENICIAN TECHNICAL SERVICES LLC
          </Typography>
          <Typography variant="body2">Central Plaza, Office 399, Dubai Investment Park 1</Typography>
          <Typography variant="body2">+971 4 8882 9464 B4</Typography>
        </Box>
        <Box>
          <img src={phoenicianLogo} alt="Phoenician Logo" style={{ height: '60px' }} />
        </Box>
      </Box>
  
      {/* Summary Section with Count and Amount columns */}
      <Typography variant="h5" fontWeight="bold" sx={{ color: '#A2732E' }} align="center" mb={1}>
        Yearly Summary Report
      </Typography>
  
      <Typography variant="h6" align="center" mb={3}>
        {year}
      </Typography>
  
      {yearlyData ? (
        <>
          {/* Summary Section */}
          <TableContainer component={Paper} elevation={0} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell width="60%"></TableCell>
                  <TableCell align="center" width="20%"><Typography fontWeight="bold">Count</Typography></TableCell>
                  <TableCell align="right" width="20%"><Typography fontWeight="bold">Amount</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Active Projects</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Valid Service Agreements
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.monthly_details.reduce((sum, month) => sum + month.active_projects_count, 0)}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.activeProjects.amount)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Waived by Management</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Projects without Operation for the month
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.waivedByManagement?.count || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.waivedByManagement?.amount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">To be Invoiced</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                        Operation for the Month
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {Math.max(0, yearlyData.summary.activeProjects.count - (yearlyData.summary.waivedByManagement?.count || 0))}
                  </TableCell>
                  <TableCell align="right">AED {formatCurrency(
                    Math.max(0, yearlyData.summary.activeProjects.amount - (yearlyData.summary.waivedByManagement?.amount || 0))
                  )}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">No Completion Certificate</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                        No Completion Certificate Received from site
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.noCompletionCertificate?.count || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.noCompletionCertificate?.amount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Completion Certificates</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Completion Certificate is received from site.
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.completionCertificates.count}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.completionCertificates.amount)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Invoice Not Processed</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Invoice already issued but not yet processed for approval/payment
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.invoiceNotProcessed?.count || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.invoiceNotProcessed?.amount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Invoice Processed</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Invoice is already uploaded to Portal or submitted to FM for approval
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.invoiceProcessed?.count || 0}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.invoiceProcessed?.amount || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Payment Received</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Payment is already reflected to PTS' Bank Statement
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{yearlyData.summary.paymentsReceived.count}</TableCell>
                  <TableCell align="right">AED {formatCurrency(yearlyData.summary.paymentsReceived.amount)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography fontWeight="medium">Balance Receivable</Typography>
                      <Typography variant="caption" sx={{ ml: 2, fontStyle: 'italic', color: '#A2732E' }}>
                      Balance from Invoice-Processed
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {(yearlyData.summary.invoiceProcessed?.count || 0) - yearlyData.summary.paymentsReceived.count}
                  </TableCell>
                  <TableCell align="right">AED {formatCurrency(
                    (yearlyData.summary.invoiceProcessed?.amount || 0) - yearlyData.summary.paymentsReceived.amount
                  )}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
  
          <Divider sx={{ mb: 3 }} />
  
          {/* Monthly Breakdown (Amount) */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Summary (Amount)
          </Typography>
  
          <TableContainer component={Paper} elevation={0} sx={{ mb: 4, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell><Typography fontWeight="bold">Month</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Active Projects</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Waived by Management</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">To be Invoiced</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">No Completion certificate</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Received Completion certificate</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Invoice-Not Processed</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Invoice-Processed</Typography></TableCell>
                  
                  <TableCell align="right"><Typography fontWeight="bold">Payment Received</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Balance Receivable</Typography></TableCell>
                </TableRow>
                {/* Explanatory row for header meanings with gold color */}
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell><Typography variant="caption" sx={{ fontStyle: 'italic' }}></Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Valid Service Agreements</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Projects without Operation for the month
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Operation for the Month</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>No received Completion Certificate from Site
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}> Completion Certificate is received from site. 
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Invoice already issued but not yet processed for approval/payment</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Invoice is already uploaded to Portal or submitted to FM for approval</Typography></TableCell>
                  
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Payment is already reflected to PTS' Bank Statement</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Balance from Invoice-Processed</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearlyData.monthly_details.map((month, index) => {
                  // Calculate Balance Receivable as Invoice Processed - Payment Received
                  const balanceReceivable = (month.processed_amount || 0) - (month.paid_amount || 0);
                  
                  return (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
            <TableCell component="th" scope="row">{month.month.trim()}</TableCell>
            <TableCell align="right">{formatCurrency(month.active_projects_amount)}</TableCell>
            {/* Waived by Management in red */}
            <TableCell align="right" sx={{ color: '#ae2732' }}>{formatCurrency(month.waived_amount)}</TableCell>
            <TableCell align="right">{formatCurrency(
              // Calculate To be Invoiced as Valid PO - Waived
              Math.max(0, (month.active_projects_amount || 0) - (month.waived_amount || 0))
            )}</TableCell>
            {/* No Completion Certificate in red */}
            <TableCell align="right" sx={{ color: '#ae2732' }}>{formatCurrency(month.no_cc_amount)}</TableCell>
            <TableCell align="right">{formatCurrency(month.cc_amount)}</TableCell>
            {/* Invoice Not Processed in red */}
            <TableCell align="right" sx={{ color: '#ae2732' }}>{formatCurrency(month.not_processed_amount)}</TableCell>
            <TableCell align="right">{formatCurrency(month.processed_amount)}</TableCell>
            
            <TableCell align="right">{formatCurrency(month.paid_amount)}</TableCell>
            {/* Balance Receivable in red */}
            <TableCell align="right" sx={{ color: '#ae2732' }}>{formatCurrency(balanceReceivable)}</TableCell>
          </TableRow>
                  );
                })}
                {/* Total row */}
                <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
  <TableCell component="th" scope="row"><Typography fontWeight="bold">Total</Typography></TableCell>
  
  {/* Active Projects - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.active_projects_amount > 0)
        .reduce((sum, month) => sum + month.active_projects_amount, 0)
    )}
  </Typography></TableCell>
  
  {/* Waived by Management - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.waived_amount > 0)
        .reduce((sum, month) => sum + (month.waived_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* To be Invoiced - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => (month.active_projects_amount - (month.waived_amount || 0)) > 0)
        .reduce((sum, month) => sum + Math.max(0, month.active_projects_amount - (month.waived_amount || 0)), 0)
    )}
  </Typography></TableCell>
  
  {/* No Completion Certificate - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.no_cc_amount > 0)
        .reduce((sum, month) => sum + (month.no_cc_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* Completion Certificate - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.cc_amount > 0)
        .reduce((sum, month) => sum + (month.cc_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* Not Processed - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.not_processed_amount > 0)
        .reduce((sum, month) => sum + (month.not_processed_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* Processed - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.processed_amount > 0)
        .reduce((sum, month) => sum + (month.processed_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* Oracle - Sum only months with movement */}
  
  
  {/* Paid - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => month.paid_amount > 0)
        .reduce((sum, month) => sum + (month.paid_amount || 0), 0)
    )}
  </Typography></TableCell>
  
  {/* Balance Receivable - Sum only months with movement */}
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(
      yearlyData.monthly_details
        .filter(month => (month.processed_amount - month.paid_amount) > 0)
        .reduce((sum, month) => sum + Math.max(0, (month.processed_amount || 0) - (month.paid_amount || 0)), 0)
    )}
  </Typography></TableCell>
</TableRow>
              </TableBody>
            </Table>
          </TableContainer>
  
          {/* Monthly Breakdown (Count) section with gold-colored explanatory row for headers and reordered columns */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Summary (Count)
          </Typography>
  
          <TableContainer component={Paper} elevation={0} sx={{ mb: 3, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell><Typography fontWeight="bold">Month</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Active Projects</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Waived by Management</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">To be Invoiced</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">No Completion certificate</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Received Completion certificate</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Invoice-Not Processed</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Invoice-Processed</Typography></TableCell>
                  
                  <TableCell align="right"><Typography fontWeight="bold">Payment Received</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Balance Receivable</Typography></TableCell>
                </TableRow>
                {/* Explanatory row for header meanings with gold color */}
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell><Typography variant="caption" sx={{ fontStyle: 'italic' }}></Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Valid Service Agreements</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Projects without Operation for the month
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Operation for the Month</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>No received Completion Certificate from Site
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}> Completion Certificate is received from site. 
                  </Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Invoice already issued but not yet processed for approval/payment</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Invoice is already uploaded to Portal or submitted to FM for approval</Typography></TableCell>
                  
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Payment is already reflected to PTS' Bank Statement</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption" sx={{ fontStyle: 'italic', color: '#A2732E' }}>Balance from Invoice-Processed</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearlyData.monthly_details.map((month, index) => {
                  // Calculate "To be Invoiced" count for each month
                  const toBeInvoicedCount = Math.max(0, (month.active_projects_count || 0) - (month.waived_count || 0));
                  
                  // Calculate Balance Receivable count as Processed - Paid
                  const receivableCount = (month.processed_count || 0) - (month.paid_count || 0);
                  
                  return (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
            <TableCell component="th" scope="row">{month.month.trim()}</TableCell>
            <TableCell align="center">{month.active_projects_count}</TableCell>
            {/* Waived count in red */}
            <TableCell align="center" sx={{ color: '#ae2732' }}>{month.waived_count}</TableCell>
            <TableCell align="center">{toBeInvoicedCount}</TableCell>
            {/* No CC count in red */}
            <TableCell align="center" sx={{ color: '#ae2732' }}>{month.no_cc_count}</TableCell>
            <TableCell align="center">{month.cc_count}</TableCell>
            {/* Not Processed count in red */}
            <TableCell align="center" sx={{ color: '#ae2732' }}>{month.not_processed_count}</TableCell>
            <TableCell align="center">{month.processed_count}</TableCell>
            
            <TableCell align="center">{month.paid_count}</TableCell>
            {/* Receivable count in red */}
            <TableCell align="center" sx={{ color: '#ae2732' }}>{receivableCount}</TableCell>
          </TableRow>
                  );
                })}
                {/* Total row */}
                <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
                  <TableCell component="th" scope="row"><Typography fontWeight="bold">Total</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
  {yearlyData.monthly_details.reduce((sum, month) => sum + month.active_projects_count, 0)}
</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + (month.waived_count || 0), 0)}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {/* Calculate total "To be Invoiced" count */}
                    {Math.max(
                      0, 
                      Math.max(...yearlyData.monthly_details.map(m => m.active_projects_count)) - 
                      yearlyData.monthly_details.reduce((sum, month) => sum + (month.waived_count || 0), 0)
                    )}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + (month.no_cc_count || 0), 0)}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + month.cc_count, 0)}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + (month.not_processed_count || 0), 0)}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + (month.processed_count || 0), 0)}
                  </Typography></TableCell>
                  
                  <TableCell align="center"><Typography fontWeight="bold">
                    {yearlyData.monthly_details.reduce((sum, month) => sum + month.paid_count, 0)}
                  </Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">
                    {Math.max(0, 
                      yearlyData.monthly_details.reduce((sum, month) => sum + (month.processed_count || 0), 0) - 
                      yearlyData.monthly_details.reduce((sum, month) => sum + month.paid_count, 0)
                    )}
                  </Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            Select year and click Apply to view report
          </Typography>
        </Box>
      )}
    </>
  );

  // Render the project report content
  const renderProjectReport = () => (
    <>
      {/* Header with Logo */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#A2732E' }}>
            PHOENICIAN TECHNICAL SERVICES LLC
          </Typography>
          <Typography variant="body2">Central Plaza, Office 399, Dubai Investment Park 1</Typography>
          <Typography variant="body2">+971 4 8882 9464 B4</Typography>
        </Box>
        <Box>
          <img src={phoenicianLogo} alt="Phoenician Logo" style={{ height: '60px' }} />
        </Box>
      </Box>

      <Typography variant="h5" fontWeight="bold" sx={{ color: '#A2732E' }} align="center" mb={1}>
        Project Summary
      </Typography>
      
      {projectData ? (
        <>
          {/* Project Details Section */}
          <Box sx={{ mb: 4 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ width: '40%', fontWeight: 'bold' }}>
                      Project Name
                    </TableCell>
                    <TableCell>{projectData.project.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      PO No.
                    </TableCell>
                    <TableCell>{projectData.project.po_number}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      Service Type
                    </TableCell>
                    <TableCell>{projectData.project.service_type}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      Start
                    </TableCell>
                    <TableCell>{projectData.project.start_date}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      End
                    </TableCell>
                    <TableCell>{projectData.project.end_date}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      Amount
                    </TableCell>
                    <TableCell>AED {formatCurrency(projectData.project.total_amount)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Monthly Breakdown */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Monthly Invoice Status
          </Typography>

          <TableContainer component={Paper} elevation={0} sx={{ mb: 4, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell><Typography fontWeight="bold">Month</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Amount</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Invoice No</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">CC</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Paid</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">Receivable</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Status</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectData.monthly_details.map((month, index) => (
                  <TableRow key={index} sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                    backgroundColor: month.status === 'Pending' ? 'rgba(255, 235, 235, 0.7)' : 
                                     month.status === 'Scheduled' ? 'rgba(235, 235, 255, 0.7)' : 
                                     undefined
                  }}>
                    <TableCell component="th" scope="row">{month.month}</TableCell>
                    <TableCell align="right">{formatCurrency(month.amount)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        color: month.invoice_no.includes('Expected') ? 'text.secondary' : 'inherit',
                        fontStyle: month.invoice_no.includes('Expected') ? 'italic' : 'normal'
                      }}>
                        {month.invoice_no}
                      </Typography>
                    </TableCell>
                    <TableCell>{month.cc || '–'}</TableCell>
                    <TableCell align="right">{formatCurrency(month.paid)}</TableCell>
                    <TableCell align="right">{formatCurrency(month.receivable)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        color: 
                          month.status === 'Paid' ? '#2e7d32' :
                          month.status === 'Pending' ? '#d32f2f' :
                          month.status === 'Scheduled' ? '#1976d2' : 'text.secondary'
                      }}>
                        {month.status}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
  <TableCell component="th" scope="row"><Typography fontWeight="bold">Total</Typography></TableCell>
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(projectData.monthly_details.reduce((sum, month) => sum + parseFloat(month.amount || 0), 0))}
  </Typography></TableCell>
  <TableCell></TableCell>
  <TableCell></TableCell>
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(projectData.monthly_details.reduce((sum, month) => sum + parseFloat(month.paid || 0), 0))}
  </Typography></TableCell>
  <TableCell align="right"><Typography fontWeight="bold">
    {formatCurrency(projectData.monthly_details.reduce((sum, month) => 
      (month.status === 'Pending' || !month.invoice_no.includes('Expected')) ? 
      sum + parseFloat(month.receivable || 0) : sum, 0))}
  </Typography></TableCell>
  <TableCell></TableCell>
</TableRow>
              </TableBody>

            </Table>
          </TableContainer>
        </>
      ) : (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            Select a project to view its report
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ py: 3, px: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Controls Bar */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 2, 
          borderRadius: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, color: '#ae2732' }}>
            {viewType === 'monthly' ? 'Monthly Project Report' : 
             viewType === 'yearly' ? 'Yearly Summary Report' : 
             'Project Summary Report'}
          </Typography>
          <Button 
            variant={viewType === 'monthly' ? "contained" : "outlined"}
            size="small"
            onClick={() => handleViewTypeChange('monthly')}
            sx={{ 
              textTransform: 'none',
              backgroundColor: viewType === 'monthly' ? '#ae2732' : 'transparent',
              color: viewType === 'monthly' ? 'white' : '#ae2732',
              '&:hover': {
                backgroundColor: viewType === 'monthly' ? '#8e1f26' : 'transparent',
              }
            }}
          >
            Monthly
          </Button>
          <Button 
            variant={viewType === 'yearly' ? "contained" : "outlined"}
            size="small"
            onClick={() => handleViewTypeChange('yearly')}
            sx={{ 
              textTransform: 'none',
              backgroundColor: viewType === 'yearly' ? '#ae2732' : 'transparent',
              color: viewType === 'yearly' ? 'white' : '#ae2732',
              '&:hover': {
                backgroundColor: viewType === 'yearly' ? '#8e1f26' : 'transparent',
              }
            }}
          >
            Yearly
          </Button>
          <Button 
            variant={viewType === 'project' ? "contained" : "outlined"}
            size="small"
            onClick={() => handleViewTypeChange('project')}
            sx={{ 
              textTransform: 'none',
              backgroundColor: viewType === 'project' ? '#ae2732' : 'transparent',
              color: viewType === 'project' ? 'white' : '#ae2732',
              '&:hover': {
                backgroundColor: viewType === 'project' ? '#8e1f26' : 'transparent',
              }
            }}
          >
            Project
          </Button>
        </Box>

        {viewType === 'monthly' ? (
          // Monthly view controls
          <>
            <FormControl size="small" sx={{ minWidth: 100, maxWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
                {years.map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120, maxWidth: 150 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month">
                {months.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Add this FormControl component in your controls area */}
<FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel>Export As</InputLabel>
  <Select 
    value={exportFormat} 
    onChange={(e) => setExportFormat(e.target.value)} 
    label="Export As"
  >
    <MenuItem value="excel">Excel</MenuItem>
    <MenuItem value="pdf">PDF</MenuItem>
  </Select>
</FormControl>
            
            <Button 
              variant="contained"
              onClick={fetchMonthlyReport}
              size="small"
              startIcon={<RefreshIcon />}
              sx={{ 
                textTransform: 'none',
                minWidth: '100px',
                backgroundColor: '#ae2732',
                '&:hover': { backgroundColor: '#8e1f26' }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Apply'}
            </Button>
            
            <Button 
  variant="outlined" 
  onClick={exportFormat === 'pdf' ? handleMonthlyPdfExport : handleMonthlyExport}
  size="small"
  startIcon={exportFormat === 'pdf' ? <PictureAsPdfIcon /> : <DownloadIcon />}
  sx={{ 
    textTransform: 'none',
    minWidth: '100px',
    color: exportFormat === 'pdf' ? '#d32f2f' : '#ae2732',
    borderColor: exportFormat === 'pdf' ? '#d32f2f' : '#ae2732',
    '&:hover': {
      borderColor: exportFormat === 'pdf' ? '#b71c1c' : '#8e1f26',
      color: exportFormat === 'pdf' ? '#b71c1c' : '#8e1f26'
    }
  }}
  disabled={loading || !monthlyData}
>
  {loading ? <CircularProgress size={20} /> : `Save as ${exportFormat.toUpperCase()}`}
</Button>
          </>
        ) : viewType === 'yearly' ? (
          // Yearly view controls
          <>
            <FormControl size="small" sx={{ minWidth: 100, maxWidth: 120 }}>
      <InputLabel>Year</InputLabel>
      <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year">
        {years.map(y => (
          <MenuItem key={y} value={y}>{y}</MenuItem>
        ))}
      </Select>
    </FormControl>
            
    <Button 
      variant="contained"
      onClick={fetchYearlyReport}
      size="small"
      startIcon={<RefreshIcon />}
      sx={{ 
        textTransform: 'none',
        minWidth: '100px',
        backgroundColor: '#ae2732',
        '&:hover': { backgroundColor: '#8e1f26' }
      }}
      disabled={loading}
    >
      {loading ? <CircularProgress size={20} /> : 'Apply'}
    </Button>


    <FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel>Export As</InputLabel>
  <Select 
    value={exportFormat} 
    onChange={(e) => setExportFormat(e.target.value)} 
    label="Export As"
  >
    <MenuItem value="excel">Excel</MenuItem>
    <MenuItem value="pdf">PDF</MenuItem>
  </Select>
</FormControl>
            
<Button 
  variant="outlined" 
  onClick={exportFormat === 'pdf' ? handleYearlyPdfExport : handleYearlyExport}
  size="small"
  startIcon={exportFormat === 'pdf' ? <PictureAsPdfIcon /> : <DownloadIcon />}
  sx={{ 
    textTransform: 'none',
    minWidth: '100px',
    color: exportFormat === 'pdf' ? '#d32f2f' : '#ae2732',
    borderColor: exportFormat === 'pdf' ? '#d32f2f' : '#ae2732',
    '&:hover': {
      borderColor: exportFormat === 'pdf' ? '#b71c1c' : '#8e1f26',
      color: exportFormat === 'pdf' ? '#b71c1c' : '#8e1f26'
    }
  }}
  disabled={loading || !yearlyData}
>
  {loading ? <CircularProgress size={20} /> : `Save as ${exportFormat.toUpperCase()}`}
</Button>
          </>
        ) : (
          // Project view controls
          <>
            <Autocomplete
              sx={{ width: 300 }}
              options={projects}
              getOptionLabel={(option) => `${option.project_name} (${option.po_number})`}
              value={selectedProject}
              onChange={handleProjectChange}
              inputValue={projectInput}
              onInputChange={(e, newValue) => {
                setProjectInput(newValue);
                // If input is 3+ characters, search for projects
                if (newValue.length >= 3) {
                  fetchProjects(newValue);
                }
              }}
              isOptionEqualToValue={(option, value) => option.po_number === value.po_number}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Select Project" 
                  size="small"
                  placeholder="Search by project name or PO"
                />
              )}
            />
            
            <Button 
              variant="contained"
              onClick={fetchProjectReport}
              size="small"
              startIcon={<RefreshIcon />}
              sx={{ 
                textTransform: 'none',
                minWidth: '100px',
                backgroundColor: '#ae2732',
                '&:hover': { backgroundColor: '#8e1f26' }
              }}
              disabled={loading || !selectedProject}
            >
              {loading ? <CircularProgress size={20} /> : 'Apply'}
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={handleProjectExport}
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ 
                textTransform: 'none',
                minWidth: '100px',
                color: '#ae2732',
                borderColor: '#ae2732',
                '&:hover': {
                  borderColor: '#8e1f26',
                  color: '#8e1f26'
                }
              }}
              disabled={loading || !projectData}
            >
              Save
            </Button>
          </>
        )}
      </Paper>

      {/* Report Display */}
      <Paper 
  sx={a4SectionStyle} 
  ref={reportContentRef} 
  className={`report-container ${viewType === 'yearly' ? 'yearly-report-container' : ''}`}
>
  {loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="300px">
      <CircularProgress sx={{ color: '#ae2732' }} />
    </Box>
  ) : error ? (
    <Box p={3} display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
      <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
      <Typography color="error" variant="h6" gutterBottom align="center">
        Error Loading Report
      </Typography>
      <Typography color="textSecondary" align="center">
        {error}
      </Typography>
      <Button 
        variant="contained" 
        onClick={fetchReport} 
        sx={{ 
          mt: 2,
          backgroundColor: '#ae2732',
          '&:hover': {
            backgroundColor: '#8e1f26'
          }
        }}
        size="small"
      >
        Try Again
      </Button>
    </Box>
  ) : (
    // Render the appropriate report view based on viewType
    viewType === 'monthly' ? renderMonthlyReport() : 
    viewType === 'yearly' ? renderYearlyReport() : 
    renderProjectReport()
  )}
</Paper>
    </Box>
  );
};

export default ReportDashboard;